import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { api } from '../services/api';

const ACCENT = '#4f46e5';

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={s.statCard}>
      <div style={{ ...s.statValue, ...(accent ? { color: accent } : {}) }}>{value}</div>
      <div style={s.statLabel}>{label}</div>
      {sub && <div style={s.statSub}>{sub}</div>}
    </div>
  );
}

function fmt(slot) {
  const [h, m] = slot.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function Analytics() {
  const [data, setData]       = useState(null);
  const [days, setDays]       = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getAnalytics(days)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  if (loading) return <div style={{ color: '#64748b', padding: 40 }}>Loading analytics…</div>;
  if (!data)   return <div style={{ color: '#ef4444', padding: 40 }}>Failed to load analytics.</div>;

  const { totals, daily, slots, sources, tableUtil, waitlistCount, dayOfWeek } = data;

  // Fill missing dates
  const dailyMap = Object.fromEntries(daily.map(d => [d.date, d]));
  const allDates = [];
  const end = new Date(); const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const k = d.toISOString().slice(0, 10);
    allDates.push({
      date: k,
      label: new Date(k + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
      reservations: dailyMap[k]?.reservations || 0,
      covers:       dailyMap[k]?.covers || 0,
    });
  }

  // Outcomes
  const totalAll  = totals.total_all   || 0;
  const completed = totals.completed   || 0;
  const noShows   = totals.no_shows    || 0;
  const cancelled = totals.cancellations || 0;
  const pct = n => totalAll > 0 ? `${Math.round(n / totalAll * 100)}%` : '—';

  // Lead time
  const leadTime = totals.avg_lead_time != null
    ? (totals.avg_lead_time < 1 ? 'Same day' : `${Math.round(totals.avg_lead_time)} days`)
    : '—';

  // Booking source pie
  const aiCount     = sources.find(s => s.source === 'ai')?.count || 0;
  const manualCount = sources.find(s => s.source === 'manual')?.count || 0;
  const sourcePie   = [
    { name: 'AI Chatbot', value: aiCount },
    { name: 'Manual',     value: manualCount },
  ].filter(x => x.value > 0);

  // Busiest day of week (Mon → Sun)
  const dowMap  = Object.fromEntries(dayOfWeek.map(d => [d.dow, d]));
  const dowData = [1,2,3,4,5,6,0].map(i => ({
    day:      DOW[i],
    bookings: dowMap[String(i)]?.count || 0,
  }));

  const outcomes = [
    { label: 'Completed', value: completed, pct: pct(completed), color: '#10b981', bg: '#f0fdf4' },
    { label: 'No-shows',  value: noShows,   pct: pct(noShows),   color: '#f59e0b', bg: '#fffbeb' },
    { label: 'Cancelled', value: cancelled, pct: pct(cancelled), color: '#ef4444', bg: '#fef2f2' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Analytics</h1>
          <p style={s.sub}>Performance overview for your restaurant.</p>
        </div>
        <div style={s.rangeBtns}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              style={{ ...s.rangeBtn, ...(days === d ? s.rangeBtnActive : {}) }}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Key stats */}
      <div style={s.statsGrid}>
        <StatCard label="Total bookings"  value={totalAll} />
        <StatCard label="Total covers"    value={totals.total_covers || 0} />
        <StatCard label="Avg party size"  value={totals.avg_party_size || '—'} />
        <StatCard label="Avg lead time"   value={leadTime} sub="days in advance" />
      </div>

      {/* Outcomes */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={s.cardTitle}>Booking outcomes</div>
        <div style={{ display: 'flex' }}>
          {outcomes.map((o, i) => (
            <div key={o.label} style={{
              flex: 1, textAlign: 'center', padding: '8px 16px',
              borderRight: i < 2 ? '1px solid #f1f5f9' : 'none',
            }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{o.value}</div>
              <div style={{ display: 'inline-block', background: o.bg, color: o.color,
                borderRadius: 6, padding: '2px 9px', fontSize: 12, fontWeight: 700, margin: '5px 0 4px' }}>
                {o.pct}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{o.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Reservations per day */}
      <div style={s.card}>
        <div style={s.cardTitle}>Reservations per day</div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={allDates} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }}
              interval={Math.floor(allDates.length / 7)} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
            <Tooltip contentStyle={s.tooltip} />
            <Bar dataKey="reservations" fill={ACCENT} radius={[4,4,0,0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Busiest times + Busiest days + Source */}
      <div style={s.row3}>
        {/* Busiest time slots */}
        <div style={{ ...s.card, flex: 1, marginBottom: 0 }}>
          <div style={s.cardTitle}>Busiest times</div>
          {slots.length === 0 ? <div style={s.empty}>No data yet</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {slots.map((sl, i) => {
                const max = slots[0].count;
                return (
                  <div key={sl.time_slot}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>{fmt(sl.time_slot)}</span>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>{sl.count} bookings</span>
                    </div>
                    <div style={{ height: 5, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${(sl.count / max) * 100}%`, height: '100%',
                        background: ACCENT, borderRadius: 4, opacity: 1 - i * 0.06 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Busiest days of week */}
        <div style={{ ...s.card, flex: 1, marginBottom: 0 }}>
          <div style={s.cardTitle}>Busiest days</div>
          {dowData.every(d => d.bookings === 0) ? <div style={s.empty}>No data yet</div> : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={dowData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip contentStyle={s.tooltip} />
                <Bar dataKey="bookings" fill={ACCENT} radius={[4,4,0,0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Booking source */}
        {sourcePie.length > 0 && (
          <div style={{ ...s.card, width: 220, flexShrink: 0, marginBottom: 0 }}>
            <div style={s.cardTitle}>Source</div>
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={sourcePie} cx="50%" cy="50%" innerRadius={38} outerRadius={58}
                  dataKey="value" paddingAngle={3}>
                  {sourcePie.map((_, i) => <Cell key={i} fill={i === 0 ? ACCENT : '#e2e8f0'} />)}
                </Pie>
                <Tooltip contentStyle={s.tooltip} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              {sourcePie.map((item, i) => (
                <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: i === 0 ? ACCENT : '#e2e8f0', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: '#475569' }}>{item.name}</span>
                  <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Table utilisation */}
      <div style={{ ...s.card, marginTop: 16 }}>
        <div style={s.cardTitle}>Table utilisation</div>
        <div style={s.tableWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Table','Capacity','Location','Bookings','Covers','No-shows'].map(h =>
                  <th key={h} style={s.th}>{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {tableUtil.map(t => (
                <tr key={t.label}>
                  <td style={{ ...s.td, fontWeight: 700, color: '#0f172a' }}>{t.label}</td>
                  <td style={s.td}>{t.capacity}</td>
                  <td style={s.td}>{t.location}</td>
                  <td style={s.td}>{t.bookings || 0}</td>
                  <td style={{ ...s.td, fontWeight: 600 }}>{t.covers || 0}</td>
                  <td style={{ ...s.td, color: t.no_shows > 0 ? '#f59e0b' : '#94a3b8' }}>
                    {t.no_shows || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const s = {
  header:        { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  h1:            { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:           { fontSize: 14, color: '#64748b', marginTop: 4 },
  rangeBtns:     { display: 'flex', gap: 6 },
  rangeBtn:      { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: '#475569', fontWeight: 500 },
  rangeBtnActive:{ background: '#4f46e5', borderColor: '#4f46e5', color: '#fff' },

  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 },
  statCard:  { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '18px 20px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)' },
  statValue: { fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 4 },
  statLabel: { fontSize: 12, color: '#64748b', fontWeight: 500 },
  statSub:   { fontSize: 11, color: '#94a3b8', marginTop: 2 },

  card:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: '20px 24px', marginBottom: 16, boxShadow: '0 1px 4px rgba(15,23,42,0.06)' },
  cardTitle: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 },
  row3:      { display: 'flex', gap: 16, alignItems: 'flex-start' },
  empty:     { color: '#94a3b8', fontSize: 14, padding: '20px 0' },

  tableWrap: { overflowX: 'auto' },
  th:        { padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td:        { padding: '11px 12px', fontSize: 13, color: '#475569', borderBottom: '1px solid #f1f5f9' },

  tooltip:   { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13 },
};
